import React, { useState, useEffect, Fragment } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Slide from "@mui/material/Slide";
import { TransitionProps } from "@mui/material/transitions";
import { Typography, TextField, Select, MenuItem } from "@mui/material";
import DialogBoxComponent from "../../../components/DialogBoxComponent";
import { IControl } from "./modal";
import { fetchControlTypeList, createIControl, updateIControl } from "./helper";
import { useUserContext } from "../../../context/auth/UserContext";
import { useRiskAssessmentContext } from "../../../context/RiskAssessment/RiskAssessmentContext";

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const getTypographyStyles = () => ({
  //Typography styles
  fontWeight: 400,
  fontSize: "14px",
  fontStyle: "italic",
  opacity: 0.5,
});

interface ControlsFormProps {
  openDialogBox: boolean;
  list: IControl[];
  selectedRow?: IControl | null;
  handleCancel: () => void;
  handleRefresh: () => void;
}

export const ControlsForm: React.FC<ControlsFormProps> = (props) => {
  const [open, setOpen] = useState(false);
  const [formTitle, setFormTitle] = useState<string>("Create Control");
  const [controlsName, setControlsName] = useState<string>("");
  const [controlsType, setControlsType] = useState<number>(0);
  const [validationMessage, setValidationMessage] = useState<string>("");

  const [dialogState, setDialogState] = useState({
    title: "",
    body: "",
    action: "",
    actionCancel: "",
    openSuccessDialogBox: false,
  });

  const { userId, companyProfileID } = useUserContext();
  const { controlTypeList, setControlTypeList } = useRiskAssessmentContext();

  const disableForm = () => {
    return controlsName === "" || controlsType === 0;
  };

  const handleClose = () => {
    setControlsName("");
    setControlsType(0);
    props.handleCancel();
    setValidationMessage("");
  };

  const handleCloseDialog = () => {
    if (dialogState.title === "Success") {
      setDialogState({
        ...dialogState,
        openSuccessDialogBox: false,
      });
    } else {
      setDialogState({
        ...dialogState,
        openSuccessDialogBox: false,
      });
    }
  };

  const handleAdd = async () => {
    const isExsisting = props.list.filter(
      (ele) => ele.controlsName === controlsName
    );
    if (isExsisting.length > 0) {
      setValidationMessage(`${controlsName} already exists.`);
      return;
    } else {
      setValidationMessage("");
      const date = new Date();
      const payload = {
        controlsName: controlsName,
        controlsTypeId: controlsType,
        createDate: date,
        createdBy: userId,
        companyProfileID: companyProfileID,
      };
      const res = await createIControl(payload);
      handleClose();
      props.handleRefresh();
      if (res.succeeded === true) {
        setDialogState({
          title: "Success",
          body: `<strong>${controlsName}</strong> successfully created`,
          action: "OK",
          actionCancel: "",
          openSuccessDialogBox: true,
        });
      } else {
        setDialogState({
          title: "Error",
          body: res.error,
          action: "OK",
          actionCancel: "",
          openSuccessDialogBox: true,
        });
      }
    }
  };

  const handleEdit = async () => {
    const isExsisting = props.list.filter(
      (ele) =>
        ele.controlsName === controlsName &&
        ele.controlsID !== props.selectedRow?.controlsID
    );
    if (isExsisting.length > 0) {
      setValidationMessage(`${controlsName} already exists.`);
      return;
    } else {
      setValidationMessage("");
      const date = new Date();
      const payload = {
        controlsName: controlsName,
        controlsTypeId: controlsType,
        updateDate: date,
        updatedBy: userId,
        controlsID: props.selectedRow?.controlsID,
        companyProfileID: companyProfileID,
      };
      const res = await updateIControl(payload);
      handleClose();
      props.handleRefresh();
      if (res.succeeded === true) {
        setDialogState({
          title: "Success",
          body: "Control successfully updated",
          action: "OK",
          actionCancel: "",
          openSuccessDialogBox: true,
        });
      } else {
        setDialogState({
          title: "Error",
          body: res.error,
          action: "OK",
          actionCancel: "",
          openSuccessDialogBox: true,
        });
      }
    }
  };

  const getControlTypeList = async () => {
    if (controlTypeList.length <= 0) {
      const res = await fetchControlTypeList();
      setControlTypeList(res);
    }
  };

  const handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (props.selectedRow?.controlsID) {
      handleEdit();
    } else {
      handleAdd();
    }
  };

  useEffect(() => {
    setOpen(props.openDialogBox);
    if (props.openDialogBox) {
      getControlTypeList();
      if (props.selectedRow?.controlsID) {
        setFormTitle("Edit Controls");
        setControlsName(props.selectedRow.controlsName);
        setControlsType(props.selectedRow.controlsTypeId);
      } else {
        setFormTitle("Create Controls");
        setControlsName("");
        setControlsType(0);
      }
    }
  }, [props.openDialogBox]);

  return (
    <Fragment>
      <Dialog
        open={open}
        fullWidth
        maxWidth="xs"
        sx={{ borderRadius: "40px" }}
        onClose={handleClose}
        TransitionComponent={Transition}
        keepMounted
        PaperProps={{
          component: "form",
          onSubmit: handleFormSubmit,
        }}
      >
        <DialogTitle>{formTitle}</DialogTitle>
        <DialogContent>
          <Typography sx={getTypographyStyles}>Control Name</Typography>
          <TextField
            autoFocus
            margin="dense"
            fullWidth
            size="small"
            variant="outlined"
            sx={{ backgroundColor: "#E4EBEF" }}
            value={controlsName}
            onChange={(e) => setControlsName(e.target.value)}
          />
          {validationMessage && (
            <Typography variant="body2" color="error">
              {validationMessage}
            </Typography>
          )}
          <div>
            <Typography sx={getTypographyStyles}>Control Type</Typography>
            <Select
              fullWidth
              value={controlsType}
              onChange={(e) => setControlsType(e.target.value)}
            >
              {controlTypeList
                .sort((a, b) =>
                  a.controlsTypeName.localeCompare(b.controlsTypeName)
                )
                .map((ele: any) => (
                  <MenuItem key={ele.controlsTypeID} value={ele.controlsTypeID}>
                    {ele.controlsTypeName}
                  </MenuItem>
                ))}
            </Select>
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            size="small"
            variant="contained"
            className="table-btn"
            onClick={handleClose}
          >
            Cancel
          </Button>
          {props.selectedRow?.controlsID ? (
            <Button
              size="small"
              variant="contained"
              sx={{
                backgroundColor: "#0066FF",
                "&:hover": {
                  bgcolor: "#0556d0",
                },
              }}
              disabled={disableForm()}
              // onClick={handleEdit}
              type="submit"
            >
              Edit
            </Button>
          ) : (
            <Button
              size="small"
              variant="contained"
              sx={{
                backgroundColor: "#0066FF",
                "&:hover": {
                  bgcolor: "#0556d0",
                },
              }}
              disabled={disableForm()}
              // onClick={handleAdd}
              type="submit"
            >
              Create
            </Button>
          )}
        </DialogActions>
      </Dialog>
      {
        <DialogBoxComponent
          openDialogBox={dialogState.openSuccessDialogBox}
          dialogTitle={dialogState.title}
          dialogAction={dialogState.action}
          dialogBody={dialogState.body}
          handleFunction={handleCloseDialog}
        />
      }
    </Fragment>
  );
};
